// 3rd Party
import Analytics from '@opencare/analytics';
import _ from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

// Components
import { BRAND_COLORS } from 'brand-guidelines/colors';
import { NUM_DAYS_IN_WEEK } from 'common/constants';
import {
  ColoredButton,
  Div,
  Span,
  VisuallyHidden,
} from 'common/styles/oc-styled-components';

// Other
import { selectDataUser } from 'connect/selectors';
import { pluralize } from 'utils/string-utils';

import { SlotPickerTimeSlots } from '../../../common/components/SlotPicker/SlotPickerTimeSlots/SlotPickerTimeSlots';
import {
  getActiveSlots,
  getDateForScreenReader,
  getDatesToDisplay,
  getNearestDateWithSlots,
  getTimeFromStartTime,
} from '../../../common/helpers/appointment-slot-helpers';
import { useFunnelStorage } from '../../../funnel/storage/Storage';
import { selectDataClinic } from '../../connect/selectors';

import {
  ChevronLeft,
  ChevronRight,
  DatesList,
  Month,
  MonthRow,
  NavigationButton,
  Slot,
  SlotsContent,
  Weekday,
} from './styled-components';

const analytics = new Analytics();

const DEFAULT_NUM_DAYS_DISPLAYED = 14;
const NUM_WEEKS_DISPLAYED = 8;
const REQUEST_SOONER_THRESHOLD = 12;
const NUM_DAYS_DISPLAYED = NUM_WEEKS_DISPLAYED * NUM_DAYS_IN_WEEK;

// Component
const InstantBookView = ({
  allSlots,
  onRequestAppointment,
  numDaysToDisplay = DEFAULT_NUM_DAYS_DISPLAYED,
  showDateRange = false,
  showSelectedDate = false,
  className: styledComponentClassName,
}) => {
  const funnelStorage = useFunnelStorage();
  const [activeSlots, setActiveSlots] = useState({});
  const [month, setMonth] = useState();
  const [startDate, setStartDate] = useState();
  const [selectedDateIndex, setSelectedDateIndex] = useState(0);
  const [selectedSlots, setSelectedSlots] = useState([]);
  const clinic = useSelector(selectDataClinic);
  const user = useSelector(selectDataUser);

  // find the earliest start date from all available slots of practice
  useEffect(() => {
    const date = getNearestDateWithSlots(allSlots);
    const formattedDate = moment(date).startOf('day');
    setStartDate(formattedDate);
  }, [allSlots]);

  useEffect(() => {
    if (!startDate) {
      return;
    }

    const slots = getActiveSlots({ startDate, slots: allSlots, numDaysToDisplay });
    setActiveSlots(slots);
    setMonth(startDate.format('MMMM'));
  }, [startDate, allSlots, numDaysToDisplay]);

  const { dayMoments } = useMemo(
    () => getDatesToDisplay({
      startDate,
      numDaysToDisplay,
    }),
    [startDate, numDaysToDisplay],
  );

  const displayedDays = useMemo(
    () => dayMoments.map((dayMoment) => dayMoment.format('ddd[,]D')),
    [dayMoments],
  );
  const slotDays = useMemo(
    () => dayMoments.map((dayMoment) => dayMoment.format('YYYY-MM-DD')),
    [dayMoments],
  );

  const firstSlotDay = useMemo(
    () => dayMoments.length ? dayMoments.at(0) : null,
    [dayMoments],
  );
  const lastSlotDay = useMemo(
    () => dayMoments.length ? dayMoments.at(-1) : null,
    [dayMoments],
  );

  const visibleDateRange = useMemo(() => {
    if (!showDateRange || !firstSlotDay || !lastSlotDay) {
      return null;
    }

    // E.g. Dec 1
    const formatString = 'MMM D';

    return {
      startDateFormatted: firstSlotDay?.format(formatString),
      endDateFormatted: lastSlotDay?.format(formatString),
    };
  }, [showDateRange, firstSlotDay, lastSlotDay]);

  useEffect(() => {
    setSelectedSlots([]);
    for (let i = 0; i < slotDays.length; i += 1) {
      const slots = activeSlots[slotDays[i]] || [];
      if (slots.length > 0) {
        setSelectedSlots(slots);
        setSelectedDateIndex(i);
        break;
      }
    }
  }, [startDate, activeSlots, slotDays]);

  const handleShowPrevThreeDays = () => {
    const newDate = startDate
      .clone()
      .startOf('day')
      .subtract(numDaysToDisplay, 'day');
    setStartDate(newDate);
  };

  const handleShowNextThreeDays = () => {
    const newDate = startDate
      .clone()
      .startOf('day')
      .add(numDaysToDisplay, 'day');
    setStartDate(newDate);
  };

  const handleDateSelected = (dateIndex, selectedSlot) => {
    setSelectedDateIndex(dateIndex);
    setSelectedSlots(selectedSlot);
    setMonth(moment(selectedSlot[0].date).format('MMMM'));
  };

  const handleSlotSelected = (slot, slotIndex, dayIndex) => {
    onRequestAppointment(slot.id);
    const slotPosition = slotIndex * 3 + dayIndex + 1;
    analytics.trackClick({
      pageType: analytics.pageTypes.PRACTICE_PROFILE,
      pageName: clinic.name,
      name: 'instant-book',
      section: clinic.name,
      position: [slotPosition],
      userId: user?.id,
      practiceId: clinic.id,
      funnelConfigId: _.get(funnelStorage, 'config.id'),
      content: {
        date: slot.date,
        time: getTimeFromStartTime(slot.time, 'HHmm'),
      },
    });
  };

  const handleRequestTime = () => {
    onRequestAppointment();
    analytics.trackClick({
      pageType: analytics.pageTypes.PRACTICE_PROFILE,
      pageName: clinic.name,
      name: 'instant-book',
      section: clinic.name,
      userId: user?.id,
      practiceId: clinic.id,
      funnelConfigId: funnelStorage?.config?.id,
    });
  };

  const today = moment();

  const isPrevButtonDisabled = firstSlotDay?.diff(today, 'days') <= 0;
  const isNextButtonDisabled = lastSlotDay?.diff(today, 'days') >= NUM_DAYS_DISPLAYED;

  return (
    <SlotsContent
      className={styledComponentClassName || ''}
      aria-label="Choose an appointment timeslot"
    >
      <MonthRow>
        <NavigationButton
          onClick={handleShowPrevThreeDays}
          aria-label={`Previous ${numDaysToDisplay} ${pluralize(
            numDaysToDisplay,
            'day',
            'days',
          )}`}
          disabled={isPrevButtonDisabled}
        >
          <ChevronLeft aria-hidden disabled={isPrevButtonDisabled} />
        </NavigationButton>

        <Month aria-hidden>
          {visibleDateRange
            ? `${visibleDateRange.startDateFormatted} - ${visibleDateRange.endDateFormatted}`
            : month}
        </Month>

        <NavigationButton
          onClick={handleShowNextThreeDays}
          aria-label={`Next ${numDaysToDisplay} ${pluralize(
            numDaysToDisplay,
            'day',
            'days',
          )}`}
          disabled={isNextButtonDisabled}
        >
          <ChevronRight aria-hidden disabled={isNextButtonDisabled} />
        </NavigationButton>
      </MonthRow>
      <SlotsContent>
        <DatesList aria-label="Appointment dates">
          <Weekday disabled>Su</Weekday>
          <Weekday disabled>Mo</Weekday>
          <Weekday disabled>Tu</Weekday>
          <Weekday disabled>We</Weekday>
          <Weekday disabled>Th</Weekday>
          <Weekday disabled>Fr</Weekday>
          <Weekday disabled>Sa</Weekday>
          {startDate
            && displayedDays.map((day, i) => {
              const slotDay = slotDays[i];
              const slots = activeSlots[slotDay] || [];
              const dayDisplay = day.split(',');
              const isToday = moment(slotDay).isSame(moment(), 'day');

              return (
                <Slot
                  key={slotDay}
                  $isSelected={selectedDateIndex === i && Boolean(slots.length)}
                  disabled={!slots.length}
                  $isToday={isToday}
                  onClick={() => handleDateSelected(i, slots)}
                >
                  <VisuallyHidden>
                    {getDateForScreenReader(slotDay)}
                  </VisuallyHidden>
                  <p aria-hidden>{dayDisplay[1]}</p>
                </Slot>
              );
            })}
        </DatesList>

        { showSelectedDate && (
          <Div textAlign="center">
            Available times for {dayMoments[selectedDateIndex]?.format('ddd, MMM D')}
          </Div>
        )}

        <SlotPickerTimeSlots
          dayIndex={selectedDateIndex}
          handleSelect={handleSlotSelected}
          slots={selectedSlots}
        />

        {allSlots.length < REQUEST_SOONER_THRESHOLD
          && (
            <Div textAlign="center">
              <ColoredButton
                id="instant-book-request-btn"
                size="medium"
                shape="round"
                background={BRAND_COLORS.white}
                color={BRAND_COLORS.darkGreen}
                onClick={handleRequestTime}
                data-testid="instant-book-request-btn"
              >
                <Span>Request a sooner time</Span>
              </ColoredButton>
            </Div>
          )}
      </SlotsContent>
    </SlotsContent>
  );
};

InstantBookView.propTypes = {
  allSlots: PropTypes.array.isRequired,
  onRequestAppointment: PropTypes.func.isRequired,
  numDaysToDisplay: PropTypes.number,
  showDateRange: PropTypes.bool,
  showSelectedDate: PropTypes.bool,
  className: PropTypes.string,
};

export default InstantBookView;
